import { Component, computed, inject, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCheck,
  lucideCopy,
  lucideDownload,
  lucideGift,
  lucideMail,
  lucideMessageCircle,
  lucidePercent,
  lucideQrCode,
  lucideShare2,
  lucideTriangleAlert,
  lucideUsers,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { KpiCardComponent } from '../../kpi-card/kpi-card.component';
import { LoggerService } from '../../../services/logger.service';
import { QrCodeService } from '../../../services/qr-code.service';
import {
  ReferralDiscount,
  ReferralOverview,
  ReferralService,
  ReferredShop,
} from '../../../services/referral.service';
import { formatCurrencyWithSymbol, formatRate } from '../../../utils/currency.utils';
import { parseApiDate } from '../../../utils/period.utils';

const STATUS: Record<
  ReferredShop['status'],
  { label: string; variant: 'solid' | 'outline' | 'destructive' }
> = {
  registered: { label: 'Registriert', variant: 'outline' },
  selling: { label: 'Gestartet', variant: 'outline' },
  rewarded: { label: 'Belohnt', variant: 'solid' },
  not_rewarded: { label: 'Nicht belohnt', variant: 'destructive' },
};

/**
 * Einstellungen → "Schenkwerk empfehlen" (?tab=recommend): a shop shares its register
 * link and sees what its recommendations earned. It shares the link itself (copy, share sheet, WhatsApp,
 * e-mail, QR code); Schenkwerk never writes to the people it recommends to.
 */
@Component({
  selector: 'app-referral-settings',
  standalone: true,
  imports: [
    NgIcon,
    KpiCardComponent,
    HlmAlertImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmInputImports,
    HlmSkeletonImports,
    HlmTableImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      lucideCheck,
      lucideCopy,
      lucideDownload,
      lucideGift,
      lucideMail,
      lucideMessageCircle,
      lucidePercent,
      lucideQrCode,
      lucideShare2,
      lucideTriangleAlert,
      lucideUsers,
    }),
  ],
  templateUrl: './referral-settings.component.html',
})
export class ReferralSettingsComponent {
  private readonly referralService = inject(ReferralService);
  private readonly qrCodeService = inject(QrCodeService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly logger = inject(LoggerService);

  readonly overview = signal<ReferralOverview | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly copied = signal(false);
  readonly showQr = signal(false);
  /** The share sheet exists on phones and some desktop browsers only. */
  readonly canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  readonly rate = formatRate;

  readonly link = computed(() => this.overview()?.link ?? '');
  readonly welcomeRate = computed(() => {
    const o = this.overview();
    return o ? o.default_fee_rate - o.discount_points / 100 : 0;
  });
  readonly qualifyingSales = computed(() =>
    formatCurrencyWithSymbol(this.overview()?.qualifying_sales_cents).replace(',00', ''),
  );
  readonly shareText = computed(() => {
    const o = this.overview();
    if (!o) return '';
    return (
      'Ich verkaufe meine Gutscheine über Schenkwerk – digitale Geschenkgutscheine ohne ' +
      `Fixkosten. Über meinen Link zahlst du nach dem Start ${o.welcome_months} Monate lang ` +
      `nur ${formatRate(this.welcomeRate())} statt ${formatRate(o.default_fee_rate)} Gebühr:`
    );
  });
  readonly whatsappUrl = computed(
    () => `https://wa.me/?text=${encodeURIComponent(`${this.shareText()} ${this.link()}`)}`,
  );
  readonly mailtoUrl = computed(() => {
    const subject = 'Digitale Gutscheine mit Schenkwerk';
    const body = `Hallo,\n\n${this.shareText()}\n${this.link()}\n\nViele Grüße`;
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  });
  private readonly qrSvgText = computed(() =>
    this.link() ? this.qrCodeService.generateQRCode(this.link(), 220) : '',
  );
  readonly qrSvg = computed<SafeHtml>(() =>
    this.sanitizer.bypassSecurityTrustHtml(this.qrSvgText()),
  );
  readonly runningCount = computed(
    () => this.overview()?.discounts.filter((d) => d.running).length ?? 0,
  );

  constructor() {
    this.referralService.getOverview().subscribe({
      next: (overview) => {
        this.overview.set(overview);
        this.loading.set(false);
      },
      error: (err) => {
        this.logger.error('Referral overview not loaded:', err);
        this.error.set('Deine Empfehlungen konnten nicht geladen werden.');
        this.loading.set(false);
      },
    });
  }

  copyLink(): void {
    navigator.clipboard?.writeText(this.link()).then(
      () => {
        this.copied.set(true);
        setTimeout(() => this.copied.set(false), 2000);
      },
      (err) => this.logger.error('Copying the link failed:', err),
    );
  }

  share(): void {
    navigator.share({ title: 'Schenkwerk', text: this.shareText(), url: this.link() }).catch(() => {
      // Closed by the user, or not allowed here: nothing to do
    });
  }

  toggleQr(): void {
    this.showQr.update((shown) => !shown);
  }

  downloadQr(): void {
    const blob = new Blob([this.qrSvgText()], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'schenkwerk-empfehlung-qr-code.svg';
    a.click();
    URL.revokeObjectURL(url);
  }

  date(iso: string | null): string {
    return iso ? parseApiDate(iso).toLocaleDateString('de-DE') : '–';
  }

  discountLabel(d: ReferralDiscount): string {
    return d.kind === 'welcome'
      ? 'Willkommensbonus'
      : `Empfehlung${d.referred_name ? ': ' + d.referred_name : ''}`;
  }

  statusLabel(status: ReferredShop['status']): string {
    return STATUS[status].label;
  }

  statusVariant(status: ReferredShop['status']): 'solid' | 'outline' | 'destructive' {
    return STATUS[status].variant;
  }
}
